import { TrashIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useApplicationModals } from "app/common/hooks";
import { Badge } from "app/components/atoms/badge";
import { Button, IconButton } from "app/components/atoms/button";
import { FormHeader } from "app/components/atoms/layout";
import Table from "app/components/atoms/table";
import Typography from "app/components/atoms/typography";
import { InputMolecule } from "app/components/molecules/form";
import ImageWithPreview from "app/components/molecules/imageWithPreview";
import { SelectSearchMolecule } from "app/components/molecules/select";
import api from "app/integration/api";
import { useCollector, useUpdateForm } from "app/integration/common/hooks";
import MainLayout from "app/layouts/main";
import AppModals from "app/modals";
import clsx from "clsx";
import { FieldArray, Formik, getIn } from "formik";
import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import style from "style/height.module.css";
import * as Yup from "yup";

const breadcrumbItems = [
  { label: "Purchase", href: "/purchase_order" },
  { label: "Order", href: "/purchase_order" },
  { label: "Product", href: "#" },
];

function CreatePurchaseOrderManualPage() {
  const navigate = useNavigate();
  let isError = false;
  const formPurchaseOrder = useRef(null);

  const handleSuccess = (res) => {
    navigate("/purchase_order");
  };

  const [isLoading, setIsloading] = useState(false);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const { initialValues, setInitialValues, validationSchema, onSubmit } =
    useUpdateForm({
      initialValues: {
        items: [],
      },
      validationSchema: Yup.object().shape({
        items: Yup.array()
          .of(
            Yup.object().shape({
              to_order: Yup.number()
                .required("Masukkan jumlah qty barang yang ingin dibeli")
                .min(0, "Qty pembelian tidak bisa negatif."),
            })
          )
          .min(1, "Minimal harus ada 1 Item yang dibeli."),
      }),
      onSubmit: async (values) => {
        try {
          let response;
          setIsloading(true);
          if (values.items.filter((item) => item.to_order > 0).length > 0) {
            response = await api.purchaseOrder.store(
              today,
              filterSupplierID,
              values.items.map((item) => ({
                supplier_id: item.supplier_id,
                product_id: item.product_id,
                size_variant_id: item.size_variant_id,
                color_variant_id: item.color_variant_id,
                to_order: item.to_order,
              }))
            );
          } else {
            isError = true;
            setIsloading(false);
            setPayloads("errorMessage.view", {
              title: "Tambah PO",
              message: "Tidak ada order yang dapat diproses.",
            });
            openModal("errorMessage.view");
          }

          if (response && response.status == "success") {
            isError = false;
            setIsloading(false);
          }
          return response;
        } catch (error) {
          if (error?.response?.data?.message) {
            setPayloads("errorMessage.view", {
              title: "Tambah PO",
              message: error?.response?.data?.message
                ? error?.response?.data?.message
                : "Server Error",
            });
            isError = true;
            openModal("errorMessage.view");
            setIsloading(false);
          } else {
            setPayloads("errorMessage.view", {
              title: "Create Purchase Order",
              message: "Server Error",
            });
            setIsloading(false);
            navigate("/purchase_order");
          }
        }
      },
      onSuccess: () => {
        if (!isError) {
          handleSuccess();
        }
      },
    });

  const { openModal, setPayloads } = useApplicationModals();
  const [currentErrors, setCurrentErrors] = useState([]);

  function runValidations(values) {
    validationSchema
      .validate(values, { abortEarly: false })
      .then((responseData) => {
        setCurrentErrors([]);
      })
      .catch((err) => {
        setCurrentErrors(err.errors);

        setPayloads("errorMessage.view", {
          title: "Tambah Order Error",
          data: err.errors,
        });
        openModal("errorMessage.view");
      });
  }

  const [suppliers, setMSuppliers, suppliersLoader, collectMSuppliers] =
    useCollector({ modelName: "supplier" });

  const [filterSupplierID, setFilterSupplierID] = useState(0);

  const updateInitialValues = () => {
    if (formPurchaseOrder.current) {
      const values = formPurchaseOrder.current?.values;
      const items = values?.items?.map((item) => {
        return {
          ...item,
          supplier_id: filterSupplierID,
        };
      });
      formPurchaseOrder.current.setFieldValue("items", items);
    }
  };

  useEffect(() => {
    updateInitialValues();
  }, [filterSupplierID]);

  const handleSuccessProductChoose = (product) => {
    if (formPurchaseOrder.current) {
      const values = formPurchaseOrder.current?.values;

      const existingItemIndex = values.items?.findIndex(
        (existingItem) =>
          existingItem.product_id === product?.product?.id &&
          existingItem.size_variant_id === product?.variant?.size?.id &&
          existingItem.color_variant_id === product?.variant?.color?.id
      );

      if (existingItemIndex !== -1) {
        // If product already exists, increment its quantity
        values.items[existingItemIndex].to_order += 1;
      } else {
        // If product doesn't exist, add new item
        formPurchaseOrder.current.setFieldValue("items", [
          ...values.items,
          {
            supplier_id: filterSupplierID,
            product_image: product?.product?.imageUrl,
            product_name: product?.product?.name,
            product_code: product?.product?.code,
            size_variant_id: product?.variant?.size?.id,
            size_variant_name: product?.variant?.size?.name,
            color_variant_id: product?.variant?.color?.id,
            color_variant_name: product?.variant?.color?.name,
            product_id: product?.product?.id,
            to_order: 1,
          },
        ]);
      }
    }
  };

  return (
    <MainLayout
      activeSidebarNavigation="purchase"
      breadcrumbItems={breadcrumbItems}
      pageTitle="Product"
      headingButtons={[
        {
          label: "Pilih Product",
          type: "button",
          onClick: () => {
            setPayloads("product.choose");
            openModal("product.choose");
          },
        },
      ]}
    >
      <AppModals
        items={["errorMessage.view", "product.choose"]}
        onSuccess={{
          "errorMessage.view": () => fetch(),
          "product.choose": (data) => handleSuccessProductChoose(data),
        }}
      />
      <FormHeader appearance="none">
        <div className="flex gap-1 items-end">
          <div className="flex-grow">
            <SelectSearchMolecule testId="purchase-order-create-manual-filter-supplier-select"
              placeholder="Filter Supplier"
              options={suppliers
                ?.sort(function (a, b) {
                  if (a.firstName?.toLowerCase() < b.firstName?.toLowerCase())
                    return -1;
                  if (a.firstName?.toLowerCase() > b.firstName?.toLowerCase())
                    return 1;
                  return 0;
                })
                .map((supplier) => ({
                  value: supplier.id,
                  label:
                    supplier.firstName +
                    " " +
                    supplier.middleName +
                    " " +
                    supplier.lastName +
                    (supplier.nickName ? " (" + supplier.nickName + ")" : ""),
                }))}
              value={filterSupplierID}
              onChange={(e) => {
                setFilterSupplierID(e.target.value);
                updateInitialValues();
              }}
            />
          </div>
          <div className="h-full flex items-end pb-2.5">
            <IconButton testId="purchase-order-create-manual-x-mark-button"
              icon={XMarkIcon}
              onClick={(e) => {
                setFilterSupplierID(0);
                updateInitialValues();
              }}
            >
              Reset Filter
            </IconButton>
          </div>
        </div>
      </FormHeader>
      <Formik
        {...{ initialValues, validationSchema, onSubmit }}
        enableReinitialize={true}
        innerRef={formPurchaseOrder}
      >
        {({
          values,
          errors,
          status,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
          setFieldValue,
        }) => (
          <form
            className={clsx(
              "px-2 overflow-y-auto",
              style["main-content-height"]
            )}
            onSubmit={handleSubmit}
          >
            <div className="py-2 overflow-visible">
              <div className="w-full">
                <FieldArray name="items">
                  {(arrayHelpers) => (
                    <div className="">
                      <Table.Wrapper asIndex={true}>
                        <Table.Header>
                          <Table.HeaderRow>
                            <Table.Heading>Supplier</Table.Heading>
                            <Table.Heading>Nama</Table.Heading>
                            <Table.Heading>Kode</Table.Heading>
                            <Table.Heading>Warna</Table.Heading>
                            <Table.Heading>Size</Table.Heading>
                            <Table.Heading align="right">Jumlah</Table.Heading>
                            <Table.Heading></Table.Heading>
                          </Table.HeaderRow>
                        </Table.Header>
                        <Table.Body>
                          {values?.items.length > 0 &&
                            values.items
                              .map((item, key) => (
                                <Table.BodyRow key={item.id}>
                                  <Table.Cell size="xs">
                                    <p>{suppliers?.find((supplier) => supplier.id === item.supplier_id)?.nickName}</p>
                                  </Table.Cell>
                                  <Table.Cell size="xs">
                                    <div className="flex gap-2 items-center">
                                      {item?.imageUrl &&
                                        item?.imageUrl != "" ? (
                                        <ImageWithPreview
                                          src={`${process.env.REACT_APP_AWS_LINK_URL}${item?.imageUrl}`}
                                        ></ImageWithPreview>
                                      ) : null}
                                      <p>{item.product_name}</p>
                                    </div>
                                  </Table.Cell>
                                  <Table.Cell size="xs">
                                    <p>{item.product_code}</p>
                                  </Table.Cell>
                                  <Table.Cell size="xs">
                                    <p>{item.color_variant_name}</p>
                                  </Table.Cell>
                                  <Table.Cell size="xs">
                                    <p>{item.size_variant_name}</p>
                                  </Table.Cell>
                                  <Table.Cell size="xs">
                                    <InputMolecule testId="purchase-order-create-manual-items-to-order-input"
                                      hideLabel={true}
                                      type="number"
                                      name={`items[${key}].to_order`}
                                      onChange={(e) => {
                                        handleChange(e);
                                      }}
                                      onBlur={handleBlur}
                                      value={item?.to_order}
                                      errorMessage={
                                        getIn(errors, `items[${key}].to_order`) &&
                                        getIn(touched, `items[${key}].to_order`) &&
                                        getIn(errors, `items[${key}].to_order`)
                                      }
                                      align="right"
                                    />
                                  </Table.Cell>
                                  <Table.Cell size="xs">
                                    <IconButton testId="purchase-order-create-manual-trash-button"
                                      icon={TrashIcon}
                                      onClick={() => {
                                        values.items.splice(key, 1);
                                        setFieldValue("items", [...values.items]);
                                      }}
                                    >
                                      Hapus
                                    </IconButton>
                                  </Table.Cell>
                                </Table.BodyRow>
                              ))}
                        </Table.Body>
                      </Table.Wrapper>
                    </div>
                  )}
                </FieldArray>
              </div>
            </div>

            <div className="fixed bottom-0 left-0 right-0 sm:absolute sm:left-auto sm:right-4 sm:bottom-4 sm:w-auto z-40 flex justify-end border-t border-gray-100 bg-white sm:bg-transparent p-4 sm:p-0 dark:border-gray-700 dark:bg-gray-800 sm:rounded-b-lg sm:border-0">
              <Button testId="purchase-order-create-manual-submit-button"
                onClick={() => {
                  runValidations(values);
                }}
                type="submit"
                disabled={isLoading}
              >
                {isLoading ? "Loading..." : `Konfirmasi Pembelian`}
              </Button>
            </div>
          </form>
        )}
      </Formik>
    </MainLayout>
  );
}

export default CreatePurchaseOrderManualPage;
